import React, { useState, useRef, useEffect, useMemo } from 'react';
import { func, string, arrayOf, number, bool, oneOfType, shape } from 'prop-types';

import { Icon } from '@atoms';
import { Flex, Button, Typography } from '@mixins';
import { theme } from '@styles';

import { ReactComponent as Close } from '@assets/svgs/Close.svg';

import { HoveredBlock, ScrollableContainer } from '../TimeSelectingModal/styles';

const ListSelectingModal = ({
  scrollTo,
  topPosition,
  canBeClosed,
  hasSelectingButton,
  title,
  onSelect,
  onModalClose,
  items,
  selectedItem: item,
  onClear,
  clearLabel,
}) => {
  const container = useRef();
  const [selectedItem, setSelectedItem] = useState(item);

  const handleItemSelect = newItem => () => {
    setSelectedItem(newItem);
  };

  const handleModalClose = () => {
    setSelectedItem(null);
    onModalClose();
  };

  const handleClear = () => {
    setSelectedItem(null);
    onClear();
  };

  useEffect(() => {
    if (scrollTo) {
      const element = document.getElementById(`item-${scrollTo}`);

      if (element) {
        const topOffset = element.offsetTop;

        container.current.scrollTo({ top: topOffset });
      }
    }
  }, [scrollTo]);

  // Items are either plain values or a { label, value } pair when the label differs from the stored value
  const options = useMemo(
    () =>
      items.map(option =>
        typeof option === 'object' && option !== null ? option : { label: option, value: option },
      ),
    [items],
  );

  const listBlock = useMemo(
    () =>
      options.map(({ label, value }) => (
        <HoveredBlock
          id={`item-${value}`}
          key={value}
          pointer
          justifyContent="center"
          alignItems="center"
          width="100%"
          onClick={hasSelectingButton ? handleItemSelect(value) : onSelect(value)}
          px={30}
          py={10}
          isSelected={value === selectedItem}>
          <Typography variant="diatype-400" fontSize={hasSelectingButton ? 14 : 16} color={theme.color.gray[300]}>
            {label}
          </Typography>
        </HoveredBlock>
      )),
    [options, selectedItem, hasSelectingButton],
  );

  return (
    <Flex
      minWidth={180}
      zIndex={3}
      backgroundColor={theme.color.white}
      boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
      flexDirection="column"
      alignItems="center"
      position="absolute"
      top={topPosition}
      left={0}
      {...(!hasSelectingButton && { right: 0 })}
      p={hasSelectingButton ? 20 : 10}
      pt={onClear || (canBeClosed && title) ? 40 : 20}>
      {onClear && (
        <Typography
          pointer
          onClick={handleClear}
          position="absolute"
          top={14}
          right={17}
          variant="diatype-400"
          fontSize={14}
          color={theme.color.mintMinded[900]}>
          {clearLabel}
        </Typography>
      )}
      {canBeClosed && (
        <Icon
          pointer
          onClick={handleModalClose}
          position="absolute"
          SVG={Close}
          width={11}
          height={11}
          top={17}
          right={17}
        />
      )}
      <Flex alignItems="center" width="100%" flexDirection="column">
        <Typography mb={20} variant="rightserif-400" fontSize={20}>
          {title}
        </Typography>
        <ScrollableContainer
          ref={container}
          width="100%"
          px={15}
          flexDirection="column"
          maxHeight={hasSelectingButton ? 170 : 260}
          overflow="hidden">
          {listBlock}
        </ScrollableContainer>
      </Flex>
      {hasSelectingButton && (
        <Button mt={10} onClick={onSelect(selectedItem)} py={12} px={50} variant="primary">
          Apply
        </Button>
      )}
    </Flex>
  );
};

ListSelectingModal.defaultProps = {
  items: [],
  selectedItem: -1,
  canBeClosed: true,
  hasSelectingButton: true,
  topPosition: 24,
  title: '',
  scrollTo: '',
  onClear: null,
  clearLabel: 'Clear',
};

ListSelectingModal.propTypes = {
  scrollTo: oneOfType([string, number]),
  canBeClosed: bool,
  hasSelectingButton: bool,
  title: string,
  onSelect: func.isRequired,
  items: arrayOf(
    oneOfType([string, number, shape({ label: oneOfType([string, number]), value: oneOfType([string, number]) })]),
  ),
  selectedItem: oneOfType([string, number]),
  onModalClose: func.isRequired,
  topPosition: number,
  onClear: func,
  clearLabel: string,
};

export default ListSelectingModal;
